import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNil } from "lodash";

import fpWipService from "@plannel/services/fp/fp-wip-service";
import WipLocationPopup from "@plannel/pages/factory-plan/common/popup/WipLocationPopup";
import WorkOrderPopup from "@plannel/pages/factory-plan/common/popup/WorkOrderPopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import NumberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

// old SearchOptsGroup 카탈로그 중 이 화면이 쓰는 2개 — §10: 고정 카탈로그, DB 동적 표시제어 없음.
const SEARCH_FIELD_LABELS = {
  routeCdNm: "FP_ROUTE",
  woCd: "FP_WORK_ORDER"
};
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const { snackBarMessageColor } = ColorUtil;

const isBlank = v => isNil(v) || v === "";

// old validRules required 3개(routeCd/startTs/endTs) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "routeCd", label: "FP_ROUTE_CD" },
  { field: "startTs", label: "STRT_DTTM" },
  { field: "endTs", label: "END_DTTM" }
];

// 같은 행의 시작/종료 일시 역전 여부. 한쪽이 비었거나 파싱 불가면 검사 대상이 아니다(필수값 검증이 따로 잡는다).
// 빈 값은 new Date()에 넘기기 **전에** 걸러야 한다 — new Date(null)은 null을 0으로 강제 변환해
// epoch(1970-01-01)를 만들고 NaN이 아니라서, 행 추가 직후 startTs만 입력하면 거짓 역전으로 잡힌다.
const isPeriodReversed = (row) => {
  if (isBlank(row?.startTs) || isBlank(row?.endTs)) return false;

  const start = new Date(row.startTs).getTime();
  const end = new Date(row.endTs).getTime();
  return !Number.isNaN(start) && !Number.isNaN(end) && start > end;
};

const Wip = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [searchFields, setSearchFields] = useState({
    routeCdNm: reduxViewState?.routeCdNm ?? "",
    woCd: reduxViewState?.woCd ?? "",
  });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);
  const [wipLocationPopup, setWipLocationPopup] = useState({ open: false, node: null, woCd: "" });
  const [workOrderPopup, setWorkOrderPopup] = useState({ open: false, node: null, routeCd: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [wip, setWip] = useState([]);

  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => FpGridSetting({ title, viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // planScope는 서버 조회 조건, 나머지는 AG Grid 외부 필터(isExternalFilterPresent/doesExternalFilterPass)로
  // 즉시 필터(§6-1 케이스3, Route.js/Resource.js와 동일 패턴). rowData 배열 자체는 절대 바꾸지 않는다 —
  // rowData를 매번 새 배열로 갈아치우면 getRowId 없는 상태에서 grid row model이 통째로 재생성되어,
  // applyTransaction으로 추가한 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다(§12 흔한 함정 참고).
  // 각 필드의 매칭 규칙은 old 서버 WipQueryRepository.makeWhereClause와 동일하게 맞췄다:
  // routeCdNm=routeCd|routeNm 부분일치, woCd=정확일치.
  // TODO(MIG-T2): [wip/Wip] 재조회·클라필터 시 미저장 변경 경고(MSG_5142) 미이관.
  //   old: handleKeyPress에서 getViewIsUpdated(viewId)가 true면 MSG_5142 확인을 받은 뒤에만 재조회.
  //   사유: Route/Resource/Stock와 동일 — FP master 공통 패턴이라 화면별로 다르게 고치지 않는다.
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { routeCdNm, woCd } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (routeCdNm && !(hit(r.routeCd, routeCdNm) || hit(r.routeNm, routeCdNm))) return false;
    if (woCd && (r.woCd ?? "") !== woCd) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  // TODO(MIG-T2): [wip/Wip] 컬럼 자동필터(autoFilter) 미이관.
  //   old: routeCd/routeNm/woCd 컬럼에 RealGrid autoFilter: true.
  //   사유: 컬럼 필터는 Tier2(1차 유보) 표준 항목 — Route/Resource/Stock와 동일하게 미적용.
  const columnDefs = [
    // id는 axios-bigint의 useNativeBigInt로 파싱돼 typeof "bigint" — AG Grid 타입 추론이
    // number/string/boolean/date 어디에도 안 걸려 "object"로 폴백해 경고를 내므로 추론을 끈다.
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // wipId는 (planScope, wipId) UNIQUE의 업무 키. 신규 행은 null로 보내고 백엔드가 채번한다.
    { headerName: "WIP_ID", field: "wipId", colId: "wipId", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    // 공정/설비는 WipLocationPopup에서 한 번에 선택(§7 CodePickerRenderer) — 직접 타이핑은 막는다.
    // old buttonVisibleCallback: isNewGridButtonVisible 대응 — 신규 행에서만 재선택 가능(§3-4).
    { headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setWipLocationPopup({ open: true, node, woCd: node?.data?.woCd ?? "" }), hideButtonWhenSaved: true } },
    { headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", type: ["nonEditableColumn"], width: 240 },
    // resourceCdString은 WipLocationPopup이 같은 공정의 설비를 ", "로 합쳐 넣는 값(백엔드가 저장 시 역분해).
    { headerName: "FP_RESOURCE", field: "resourceCdString", colId: "resourceCdString", type: ["nonEditableColumn"], width: 180 },
    // old buttonVisibleCallback: isNewGridButtonVisible 대응 — 신규 행에서만 재선택 가능(§3-4).
    { headerName: "FP_WORK_ORDER_CODE", field: "woCd", colId: "woCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setWorkOrderPopup({ open: true, node, routeCd: node?.data?.routeCd ?? "" }), hideButtonWhenSaved: true } },
    // WIP **수량**이라 `FP_DEF_QTY`("수량")를 쓴다 — `FP_QTY`는 old 랭팩대로 "생산량"이다(2026-08-24).
    { headerName: "QTY", field: "wipQty", colId: "wipQty", width: 110, type: ["numberColumn", "rightAligned"],
      // old editFormat(로케일, 소수 5자리) 대응 — 가드는 공용 numberColumn 타입과 동일 기준.
      valueFormatter: p => (isNil(p.value) || p.value === "" || !isFinite(p.value)) ? "" : NumberUtil.formatNumberData(p, { maximumFractionDigits: 5, minimumFractionDigits: 0 }),
      cellEditor: "agNumberCellEditor", cellEditorParams: { min: 0 } },
    // actualQty는 백엔드 계산값(wipQty - remainQty) — 조회 전용.
    { headerName: "QTY_COUNT", field: "actualQty", colId: "actualQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 110,
      valueFormatter: p => (isNil(p.value) || p.value === "" || !isFinite(p.value)) ? "" : NumberUtil.formatNumberData(p, { maximumFractionDigits: 5, minimumFractionDigits: 0 }) },
    { headerName: "STRT_DTTM", field: "startTs", colId: "startTs", type: ["dateTimeColumn"], width: 150 },
    { headerName: "END_DTTM", field: "endTs", colId: "endTs", type: ["dateTimeColumn"], width: 150 },
    // 부가 정보 그룹(2줄 헤더, 접기/펼치기) — old extraGroup(expanded:false) 대응.
    // 접힘: 후공정 투입일시(old groupShowMode "always"), 펼침: 재고화 여부/배치 그룹 추가.
    {
      headerName: "FP_EXTRA_INFO", groupId: "extraInfo", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_NEXT_ROUTE_INPUT_DT", field: "releaseTs", colId: "releaseTs", type: ["dateTimeColumn"], width: 150 },
        { headerName: "FP_TO_STOCK_YN", field: "toStockYn", colId: "toStockYn", type: ["booleanColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        // batchGrpCdString도 resourceCdString과 같은 join 문자열(", ") — 백엔드가 tb_fp_wip_batch로 역분해 저장.
        { headerName: "FP_BATCH_GRP_CD", field: "batchGrpCdString", colId: "batchGrpCdString", cellClass: "stringType", width: 180, columnGroupShow: "open" }
      ]
    },
    // 수정 정보(audit) 그룹 — Route.js/Resource.js/Stock.js와 동일 FP_COL_AUDIT 그룹 패턴 재사용.
    // createUser/modifyUser는 WipQueryRepository의 z_user 2-join으로 채워진다(2026-08-06 적용 완료, §5).
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveWip = () => {
    if (!planScope) return;

    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    // planScope 단위로 전량 로드 → 공정/작업주문 검색어는 서버가 아니라 AG Grid 외부 필터(클라)에서 처리.
    fpWipService.getAll({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setWip([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setWip(results);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  // 시작/종료 일시 역전을 편집 즉시 차단 — old onEditCommit(showMessage 후 return false)과 동일 동작.
  // 공용 DefaultGridSetting.onCellValueChanged는 gridPaste + DataState.setDataState 두 가지뿐이라,
  // 화면에서 감싼 뒤 위임하는 게 코드베이스 표준 패턴이다(RunRp.js:572-656, RpPolicy.js:546-582,
  // ProdRouting.js:142-170 — 전부 잘못된 값을 setDataValue(oldValue)로 되돌린다).
  // 위임을 되돌리기보다 **먼저** 해야 DataState가 진짜 원본을 origin으로 잡고, 되돌리기로 재진입했을 때
  // "원상복구됨 → dirty 해제"(DataState.js:46-55)까지 스스로 처리한다. 순서를 바꾸면 행이 거짓 dirty로 남는다.
  const onCellValueChanged = params => {
    defaultGridMemo.onCellValueChanged(params);

    const colId = params.column.getColId();
    if (colId !== "startTs" && colId !== "endTs") return;

    if (isPeriodReversed(params.data)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_VALID_START_END_TS") });
      params.node.setDataValue(colId, params.oldValue);
    }
  };

  const insertRow = () => {
    // old defaultValue(wipQty "0", toStockYn false) + 파티션 키(planScope) 반영.
    const newRow = {
      id: null,
      planScope,
      // 백엔드 persistsWips가 null/0이면 (planScope 기준) 최대값+1로 채번한다.
      wipId: null,
      routeCd: "",
      routeNm: "",
      resourceCdString: "",
      woCd: "",
      wipQty: 0,
      // actualQty는 조회 전용이지만 백엔드가 remainQty = wipQty - actualQty로 계산하므로 0으로 시작한다.
      actualQty: 0,
      startTs: null,
      endTs: null,
      releaseTs: null,
      toStockYn: false,
      batchGrpCdString: ""
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeWip = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpWipService.remove(selectedRows)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveWip();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveWip = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2,
    // tb_fp_wip도 이 3개가 NOT NULL).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      setProgressOn(false);
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    // 시작/종료 일시 역전 검증은 저장 시점에 하지 않는다 — old와 동일하게 편집 시점
    // (onCellValueChanged)에서 되돌리는 것으로 끝낸다.
    fpWipService.upsert(allStateData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
        retrieveWip();
      });
  };

  // 재공 위치 선택 시 공정 코드/명칭 + 설비 join 문자열을 함께 write-back — old setWipLocationValues와 동일 3필드.
  const onSelectWipLocation = (row) => {
    const node = wipLocationPopup.node;
    if (node) {
      node.setDataValue("routeCd", row.routeCd);
      node.setDataValue("routeNm", row.routeNm);
      node.setDataValue("resourceCdString", row.resourceCdString);
    }
  };

  // 작업 주문 선택 시 woCd만 write-back — old setWoValues와 동일.
  const onSelectWorkOrder = (row) => {
    const node = workOrderPopup.node;
    if (node) {
      node.setDataValue("woCd", row.woCd);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true);
      saveWip();
    } else if (action === "DELETE") {
      removeWip();
    }
  };

  useEffect(() => {
    if (!gridLoading) return;
    retrieveWip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={SEARCH_FIELD_KEYS}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        <Box sx={{ mt: 1.5, ml: "auto", mr: 1.5 }}>
          <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </FilterContainer>
      <Box id="fp-wip-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={wip}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </Box>
      <WipLocationPopup
        open={wipLocationPopup.open}
        onClose={() => setWipLocationPopup({ open: false, node: null, woCd: "" })}
        planScope={planScope}
        woCd={wipLocationPopup.woCd}
        onSelect={onSelectWipLocation}
      />
      <WorkOrderPopup
        open={workOrderPopup.open}
        onClose={() => setWorkOrderPopup({ open: false, node: null, routeCd: "" })}
        planScope={planScope}
        routeCd={workOrderPopup.routeCd}
        onSelect={onSelectWorkOrder}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Wip);
